import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import Captcha from "@/components/site/Captcha";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSEO } from "@/lib/seo";

const CATS = ["general", "candidate-experience", "client-experience", "website", "process"];

export default function Feedback() {
  useSEO({
    title: "Feedback",
    description: "Share feedback about your experience with PharmacoHires — we read every message.",
    pageKey: "feedback",
  });
  const [form, setForm] = useState({ name: "", email: "", category: "general", rating: 5, message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const captchaRef = useRef(null);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const cap = captchaRef.current?.getValues() || {};
    setSubmitting(true);
    try {
      await api.post("/feedback", { ...form, rating: Number(form.rating), ...cap });
      toast.success("Thank you! Your feedback helps us improve.");
      setForm({ name: "", email: "", category: "general", rating: 5, message: "" });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
      captchaRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        overline="Feedback"
        title="Tell us how we did."
        description="Every piece of feedback — good or critical — makes us sharper. We read each one."
      />
      <section className="section-pad">
        <div className="container-page max-w-2xl">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            {done ? (
              <div className="text-center" data-testid="feedback-done">
                <h3 className="font-heading text-xl font-semibold text-slate-900">Thank you!</h3>
                <p className="mt-2 text-slate-600">We received your feedback and will follow up if needed.</p>
                <Button className="mt-5" onClick={() => setDone(false)} data-testid="feedback-another">Send another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="feedback-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="fb-name">Name</Label>
                    <Input id="fb-name" required value={form.name} onChange={onChange("name")} data-testid="feedback-name" />
                  </div>
                  <div>
                    <Label htmlFor="fb-email">Email</Label>
                    <Input id="fb-email" type="email" required value={form.email} onChange={onChange("email")} data-testid="feedback-email" />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="fb-cat">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="feedback-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Rating</Label>
                    <div className="flex items-center gap-1" data-testid="feedback-rating">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          type="button"
                          key={n}
                          onClick={() => setForm({ ...form, rating: n })}
                          data-testid={`feedback-star-${n}`}
                          className="rounded p-1 hover:bg-slate-100"
                        >
                          <Star className={`h-5 w-5 ${n <= form.rating ? "fill-secondary text-secondary" : "text-slate-300"}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <Label htmlFor="fb-msg">Your feedback</Label>
                  <Textarea id="fb-msg" required rows={5} value={form.message} onChange={onChange("message")} data-testid="feedback-message" />
                </div>
                <Captcha ref={captchaRef} testId="feedback-captcha" />
                <Button type="submit" disabled={submitting} className="bg-primary hover:bg-primary/90" data-testid="feedback-submit">
                  {submitting ? "Sending…" : "Send feedback"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
